<template>
	<div class="moment-comment-panel">
		<div class="panel-toolbar">
			<div class="summary">
				<span>评论 {{ allComment }}</span>
				<span v-if="closeComment > 0" class="pending">（{{ closeComment }} 条待审核）</span>
			</div>
		</div>

		<div class="comment-list-wrap" v-loading="loading">
			<div v-if="flatComments.length === 0 && !loading" class="empty">还没有评论，来抢沙发吧</div>
			<div v-else class="comment-list">
				<div
					v-for="item in flatComments"
					:key="item.id"
					class="comment-item"
					:style="{paddingLeft: `${item.depth * 18 + 8}px`}">
					<img class="avatar" :src="item.avatar" alt="avatar">
					<div class="detail">
						<div class="meta">
							<span class="name">{{ item.nickname }}</span>
							<span class="time">{{ item.createTime | dateFormat('YYYY-MM-DD HH:mm') }}</span>
						</div>
						<p class="content">
							<span v-if="item.parentCommentNickname" class="reply-target">@{{ item.parentCommentNickname }}</span>
							{{ item.content }}
						</p>
					</div>
				</div>
			</div>
		</div>

		<el-pagination
			v-if="totalPage > 1"
			@current-change="handlePageChange"
			:current-page="query.pageNum"
			:page-count="totalPage"
			layout="prev, pager, next"
			background
			small
			class="comment-pagination" />

		<div class="comment-form">
			<div class="identity-tip">
				<i class="user circle icon"></i>
				<span>{{ identityTip }}</span>
			</div>
			<textarea
				v-model.trim="form.content"
				rows="3"
				maxlength="250"
				placeholder="写下你的看法..."
				@keydown.ctrl.enter.prevent="submitCommentContent"></textarea>
			<div class="form-footer">
				<span class="hint">Ctrl + Enter 快速提交</span>
				<button :disabled="submitting" @click="submitCommentContent">{{ submitting ? '提交中...' : '发表评论' }}</button>
			</div>
		</div>
	</div>
</template>

<script>
import {getCommentListByQuery, submitComment} from '@/api/comment'
import {getCommentIdentity} from '@/util/guestIdentity'

export default {
	name: 'MomentCommentPanel',
	props: {
		momentId: {
			type: Number,
			required: true
		},
		visible: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			loading: false,
			submitting: false,
			allComment: 0,
			closeComment: 0,
			totalPage: 0,
			rawComments: [],
			query: {
				page: 3,
				momentId: 0,
				pageNum: 1,
				pageSize: 8
			},
			form: {
				content: ''
			},
			hasLoaded: false
		}
	},
	computed: {
		flatComments() {
			const flattened = []
			const walk = (list, depth = 0) => {
				(list || []).forEach(comment => {
					flattened.push({
						id: comment.id,
						nickname: comment.nickname,
						avatar: comment.avatar,
						createTime: comment.createTime,
						content: comment.content,
						parentCommentNickname: comment.parentCommentNickname || '',
						depth,
					})
					if (comment.replyComments && comment.replyComments.length > 0) {
						walk(comment.replyComments, depth + 1)
					}
				})
			}
			walk(this.rawComments)
			return flattened
		},
		identityTip() {
			const identity = getCommentIdentity()
			if (identity.isLoggedIn) {
				return `已登录：${identity.nickname}`
			}
			return `游客身份：${identity.nickname}`
		}
	},
	watch: {
		visible(show) {
			if (show && !this.hasLoaded) {
				this.fetchComments()
			}
			if (show) {
				this.emitSummary()
			}
		},
		momentId: {
			immediate: true,
			handler(id) {
				this.query.momentId = id
			}
		}
	},
	methods: {
		fetchComments() {
			this.loading = true
			const identity = getCommentIdentity()
			getCommentListByQuery(identity.token, this.query).then(res => {
				this.allComment = Number(res.data.allComment || 0)
				this.closeComment = Number(res.data.closeComment || 0)
				this.totalPage = Number(res.data.comments.totalPage || 0)
				this.rawComments = res.data.comments.list || []
				this.hasLoaded = true
				this.emitSummary()
			}).catch(() => {
				this.$message.error('评论加载失败')
			}).finally(() => {
				this.loading = false
			})
		},
		handlePageChange(pageNum) {
			this.query.pageNum = pageNum
			this.fetchComments()
		},
		submitCommentContent() {
			if (!this.form.content) {
				this.$message.warning('评论内容不能为空')
				return
			}
			if (this.form.content.length > 250) {
				this.$message.warning('评论内容不能超过 250 字')
				return
			}
			const identity = getCommentIdentity()
			const payload = {
				page: 3,
				momentId: this.momentId,
				parentCommentId: -1,
				content: this.form.content,
				nickname: identity.nickname,
				email: identity.email,
				avatar: identity.avatar,
				website: '',
				notice: false
			}
			this.submitting = true
			submitComment(identity.token, payload).then(res => {
				if (res.code !== 200) {
					this.$message.error(res.msg || '评论失败')
					return
				}
				this.$message.success('评论成功')
				this.form.content = ''
				this.query.pageNum = 1
				this.fetchComments()
			}).catch(() => {
				this.$message.error('评论失败')
			}).finally(() => {
				this.submitting = false
			})
		},
		emitSummary() {
			this.$emit('summary-change', {
				momentId: this.momentId,
				count: this.allComment
			})
		}
	}
}
</script>

<style scoped>
.moment-comment-panel {
	background: #fff;
	border: 1px solid #edf2f7;
	border-radius: 12px;
	padding: 10px;
}

.panel-toolbar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 8px;
}

.summary {
	font-size: 13px;
	font-weight: 600;
	color: #1f2937;
}

.pending {
	font-weight: 400;
	color: #6b7280;
}

.comment-list-wrap {
	border: 1px solid #eef2f7;
	background: #fbfdff;
	border-radius: 10px;
	max-height: 280px;
	overflow: auto;
}

.empty {
	text-align: center;
	padding: 22px 12px;
	font-size: 13px;
	color: #94a3b8;
}

.comment-list {
	padding: 4px 0;
}

.comment-item {
	display: flex;
	gap: 8px;
	padding: 8px 10px;
	border-bottom: 1px dashed #edf2f7;
}

.comment-item:last-child {
	border-bottom: 0;
}

.avatar {
	width: 28px;
	height: 28px;
	border-radius: 50%;
	flex: 0 0 28px;
	object-fit: cover;
}

.detail {
	min-width: 0;
	flex: 1;
}

.meta {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
	margin-bottom: 2px;
}

.name {
	font-size: 12px;
	font-weight: 600;
	color: #0f172a;
}

.time {
	font-size: 11px;
	color: #94a3b8;
}

.content {
	margin: 0;
	font-size: 13px;
	line-height: 1.55;
	color: #334155;
	word-break: break-word;
	white-space: pre-wrap;
}

.reply-target {
	margin-right: 4px;
	font-weight: 600;
	color: #2563eb;
}

.comment-pagination {
	text-align: center;
	margin-top: 8px;
}

.comment-form {
	margin-top: 10px;
}

.identity-tip {
	display: inline-flex;
	align-items: center;
	gap: 5px;
	font-size: 12px;
	color: #64748b;
	margin-bottom: 6px;
}

.comment-form textarea {
	width: 100%;
	border: 1px solid #dbe5ef;
	border-radius: 10px;
	padding: 9px 10px;
	resize: vertical;
	outline: none;
	font-size: 13px;
	color: #334155;
	transition: border-color .2s ease, box-shadow .2s ease;
}

.comment-form textarea:focus {
	border-color: #93c5fd;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, .12);
}

.form-footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-top: 8px;
}

.hint {
	font-size: 11px;
	color: #94a3b8;
}

.form-footer button {
	border: 0;
	background: #2563eb;
	color: #fff;
	border-radius: 8px;
	padding: 6px 14px;
	font-size: 12px;
	cursor: pointer;
	transition: background .2s ease;
}

.form-footer button:hover {
	background: #1d4ed8;
}

.form-footer button:disabled {
	background: #93c5fd;
	cursor: not-allowed;
}

@media (max-width: 768px) {
	.comment-list-wrap {
		max-height: 240px;
	}

	.form-footer {
		gap: 8px;
		flex-direction: column;
		align-items: flex-start;
	}
}
</style>
